import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { IEmbedConfiguration } from 'powerbi-client';
import { TokenType, LayoutType, IAdvancedFilterCondition, ISlicer, FilterType } from 'powerbi-models';

import { ScreenSizeService } from '@app/core/services/screen-size.service';
import { IPowerBiReport } from '../models/power-bi-report.model';
import { PowerBiApiService } from './power-bi-api.service';
import { Report } from '@app/shared/features/reports/models/report.model';
import { PowerBiFilterData, PowerBiSlicerData } from '../models/power-bi-data.model';
import { ReportFilterSchemas } from '../enums/report-filters.enum';

interface AdvancedSlicersGroup {
    table: string,
    column: string,
    visualName: string,
    conditions: IAdvancedFilterCondition[]
}

@Injectable({
    providedIn: 'root'
})
export class PowerBiConfigBuilder {
    private config: IEmbedConfiguration;

    constructor(
        private readonly powerBiApiService: PowerBiApiService,
        private readonly screenSizeService: ScreenSizeService
    ) {
        this.reset();
    }

    public setReportBaseInfo(report: Report): Observable<IPowerBiReport> {
        return this.getReportData(report)
            .pipe(
                tap((powerBIReport: IPowerBiReport) => {
                    this.config.accessToken = powerBIReport.embedToken;
                    this.config.embedUrl = powerBIReport.embedUrl + (report.page ? '&pageName=' + report.page : '');
                    this.config.id = powerBIReport.id;
                })
            );
    }

    public setFilters(filtersData: PowerBiFilterData[]): void {
        this.config.filters = (filtersData || []).map(({ value, table, column }) => ({
            $schema: ReportFilterSchemas.Basic,
            target: { table, column },
            operator: 'In',
            filterType: 1,
            values: [value]
        }));
    }

    // eslint-disable-next-line
    public setBasicSlicers(slicersData: PowerBiSlicerData[]): void {
        if (!slicersData?.length) {
            return;
        }

        const slicers = slicersData.filter(slicer => slicer.operator === undefined);

        this.config.slicers ??= [];

        const basicSlicerConfigs: ISlicer[] = (slicers || []).map(({ value, table, column, visualName }) => (
            {
                selector: {
                    $schema: ReportFilterSchemas.VisualSelector,
                    visualName
                },
                state: {
                    filters: [{
                        $schema: ReportFilterSchemas.Basic,
                        target: {
                            table,
                            column
                        },
                        operator: 'In',
                        values: Array.isArray(value) ? value : [value],
                        filterType: FilterType.Basic
                    }]
                }
            }
        ));

        for (let config of basicSlicerConfigs) {
            this.config.slicers.push(config);
        }
    }

    // handles pc activity by period report start and end date filters
    // eslint-disable-next-line
    public setAdvancedSlicers(slicersData: PowerBiSlicerData[]): void {
        if (!slicersData?.length) {
            return;
        }

        const slicers = slicersData.filter(slicer => slicer.operator !== undefined);
        if (!slicers.length) {
            return;
        }
        const groupedSlicers: AdvancedSlicersGroup[] = [];

        this.config.slicers ??= [];

        for (let slicer of slicers) {
            // eslint-disable-next-line max-len
            const i = groupedSlicers.findIndex(a => a.column === slicer.column && a.table === slicer.table && a.visualName === slicer.visualName);
            if (i > -1 && !groupedSlicers[i].conditions.some(a => a.operator === slicer.operator && a.value === slicer.value)) {
                groupedSlicers[i].conditions.push({
                    operator: slicer.operator,
                    // eslint-disable-next-line max-len
                    value: this.isDate(slicer.value as string) ? this.formatDate(slicer.value as string, slicer.operator) : slicer.value as string
                });
            } else {
                groupedSlicers.push({
                    table: slicer.table,
                    column: slicer.column,
                    visualName: slicer.visualName,
                    conditions: [{
                        operator: slicer.operator,
                        // eslint-disable-next-line max-len
                        value: this.isDate(slicer.value as string) ? this.formatDate(slicer.value as string, slicer.operator) : slicer.value as string
                    }]
                });
            }
        }

        const advancedSlicerConfigs: ISlicer[] = groupedSlicers.map(({ table, column, visualName, conditions }) => (
            {
                selector: {
                    $schema: ReportFilterSchemas.VisualSelector,
                    visualName
                },
                state: {
                    filters: [{
                        $schema: ReportFilterSchemas.Advanced,
                        target: {
                            table,
                            column
                        },
                        filterType: FilterType.Advanced,
                        logicalOperator: 'And',
                        conditions
                    }]
                }
            }
        ));

        if (advancedSlicerConfigs.length) {
            for (let config of advancedSlicerConfigs) {
                this.config.slicers.push(config);
            }
        }
    }

    public setPageNavigation(pageNavigationVisible: boolean): void {
        this.config.settings.panes = {
            pageNavigation: { visible: pageNavigationVisible }
        };
    }

    public reset(): void {
        this.config = {
            type: 'report',
            tokenType: TokenType.Embed,
            settings: {
                layoutType: this.screenSizeService.isScreenWidthMedium()
                    ? LayoutType.MobilePortrait
                    : LayoutType.Master
            }
        };
    }

    public getConfig(): IEmbedConfiguration {
        const result = this.config;
        this.reset();

        return result;
    }

    private getReportData(report: Report): Observable<IPowerBiReport> {
        return this.powerBiApiService.getReportById(report.reportId);
    }

    private formatDate(value: string, operator: string): string {
        const [monthStr, dayStr, yearStr] = value.split('/');
        const month = Number.parseInt(monthStr, 10);
        const day = Number.parseInt(dayStr, 10);
        const year = Number.parseInt(yearStr, 10);
        let isoDate;
        switch (operator) {
            case 'GreaterThanOrEqual':
                isoDate = new Date(Date.UTC(year, month - 1, day, 5)).toISOString();
                break;
            case 'LessThan':
                isoDate = new Date(Date.UTC(year, month - 1, day + 1, 5)).toISOString();
                break;
            default:
                isoDate = new Date(Date.UTC(year, month - 1, day + 1, 5)).toISOString();
        }

        return isoDate;
    }

    private isDate(text: string): boolean {
        const date = new Date(text);

        return !Number.isNaN(date.getTime());
    }
}
